import { type ExtendedAppointmentAttachment } from "@repo/api";
import { LightMode } from "@repo/skapa";
import { t } from "@repo/translation";

import { type FileLoadingEnum, getFileType } from "../utils";
import { FileListItem } from "./FileListItem";

export const AppointmentFileItem = ({
  file,
  loading,
  loadingFileId,
  editable,
  onDownload,
}: {
  file: ExtendedAppointmentAttachment;
  loading?: FileLoadingEnum;
  loadingFileId?: number;
  editable?: boolean;
  onDownload: (
    name: string,
    attachmentId: number,
    action: "OPEN" | "DOWNLOAD",
    mimeType: string,
    url?: string
  ) => void;
}) => {
  const url = file.fileUrl;
  const uploadedDate = file.currentTimestamp;
  const isPendingAppointmentFile = file.fileStatus === "PENDING";
  const isRejectedAppointmentFile = file.fileStatus === "REJECTED";
  const isUploadErrorAppointmentFile = file.fileStatus === "UPLOAD_ERROR";
  const isLegacyUnavailableAppointmentFile =
    file.fileStatus === "LEGACY_UNAVAILABLE";
  const isAppointmentFileAbsent =
    isPendingAppointmentFile ||
    isRejectedAppointmentFile ||
    isUploadErrorAppointmentFile ||
    isLegacyUnavailableAppointmentFile;

  const getStatus = () => {
    if (isPendingAppointmentFile) {
      return {
        text: t("label.attachment_pending"),
      };
    }
    if (isRejectedAppointmentFile) {
      return {
        text: t("label.attachment_rejected"),
        color: LightMode.colourSemanticNegative,
      };
    }
    if (isUploadErrorAppointmentFile) {
      return {
        text: t("label.attachment_upload_error"),
        color: LightMode.colourSemanticNegative,
      };
    }
    if (isLegacyUnavailableAppointmentFile) {
      return {
        text: t("label.attachment_unavailable"),
        color: LightMode.colourSemanticNegative,
      };
    }
    return null;
  };

  const allowDownload = !isAppointmentFileAbsent && Boolean(onDownload);
  const fileType = getFileType(file.fileName);

  return (
    <FileListItem
      attachmentId={file.attachmentId}
      fileName={file.fileName}
      fileType={fileType}
      uploadedDate={uploadedDate}
      status={getStatus()}
      allowPreview={allowDownload}
      onDownload={
        allowDownload
          ? (mode) => {
              onDownload(
                file.fileName,
                file.attachmentId,
                mode,
                fileType ?? "application/octet-stream",
                url
              );
            }
          : undefined
      }
      editable={editable}
      loading={loading}
      loadingFileId={loadingFileId}
    />
  );
};
